import type { Modal } from './types/modal';
import { type ModalOptions, type ModalProps } from './types/modal';
import { type ModalInstance } from './types/modalInstance';
import { modals } from './modals';
import { useModalStore } from './store';
import type { Component } from 'vue';
import { defineModal } from './defineModal';

export const useModalInstance = <T extends Modal<any, any>>(
  modal: ModalInstance<T> | T,
  options?: ModalOptions,
) => {
  const store = useModalStore();
  const isModalInstance = (instance: any): instance is ModalInstance<any> => {
    return 'id' in instance;
  };

  let instance: ModalInstance<any>;

  if (!isModalInstance(modal)) {
    instance = {
      ...modal,
      props: {},
      container: options?.container || 'default',
      id: Symbol(),
      resolver: () => {},
      value: Promise.resolve(modal.defaultValue),
    };
  } else {
    instance = modal;
  }

  return {
    // ? TS Stopped inferring the props from the component.
    // ? any is a temp solution till types can be fixed.
    open: (props: ModalProps<T> | any) => {
      instance.value = new Promise((resolve) => {
        instance.resolver = resolve;
      });
      instance.props = {
        timeout: options?.timeout,
        ...props,
      };

      let queue = store.open[instance.container];

      if (!queue) {
        store.open[instance.container] = [];

        queue = store.open[instance.container];
      }

      queue.push(instance);

      return instance.value;
    },
    close: () => {
      const queue = store.open[instance.container];
      const index = queue.findIndex((m) => m.id === instance.id);

      if (index >= 0) {
        queue.splice(index, 1);
      }
    },
    resolve: (value: any) => {
      instance.resolver?.(value);
    },
  };
};

export const useModalComponent = (component: Component, options?: ModalOptions) => {
  const modal = defineModal(component, undefined);

  return useModalInstance(modal, options);
};

export const useModal = <N extends keyof typeof modals>(name: N, options?: ModalOptions) => {
  const modal = modals[name];

  if (!modal) {
    throw new Error(`Modal ${name} not found`);
  }

  return useModalInstance(modal, options);
};
